import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { Link } from 'react-router-dom';
import './TrialLock.scss';

// Statuses where creation is blocked server-side (see requireActiveSubscription / sendBillingBlocked).
const BLOCKED_STATUSES = ['suspended', 'past_due', 'canceled', 'for_deletion'];

interface TrialLockProps {
    // Whether this resource TYPE is locked on the no-card limited trial (default true).
    // Pass locked={false} for resources that ARE allowed on the trial (instances, keypairs, Postgres)
    // so the control is only disabled when the account is suspended / out of credits.
    locked?: boolean;
    // Instances/Postgres were allowed by the legacy trial, but are paid console actions.
    paidResource?: boolean;
    // Override the tooltip for the locked-resource case.
    reason?: string;
    children: React.ReactElement;
}

// Disables a create/action control and shows a "why" tooltip when it would be rejected server-side:
//  - the resource type is locked on the limited trial (locked && tier === 'trial_no_card'), or
//  - the account is suspended / out of trial credits (any blocked billing status).
// Otherwise it renders the control unchanged. It adds NO wrapper element (the create button is a
// fixed FAB; a positioned/filtered wrapper would become its containing block); it clones the control
// and applies opacity, which does not create a containing block.
export function useResourceCreationLock(locked = true, paidResource = locked, reason?: string): string | null {
    const forecast = useSelector((s: RootState) => s.billingForecast.forecast);
    const forecastStatus = useSelector((s: RootState) => s.billingForecast.status);
    // Wait for the billing forecast only while it is on its way: one that failed to load leaves the decision to the
    // server's own answer (402/403), instead of disabling every create control for the rest of the session.
    if (paidResource && !forecast) return forecastStatus === 'failed' ? null : 'Checking billing access…';
    const isTrial = forecast?.tier === 'trial_no_card';
    if (forecast?.suspended_by_admin) return 'This account is suspended. Contact support.';
    if (forecast?.status && BLOCKED_STATUSES.includes(forecast.status)) return 'Billing paused. Resolve billing to continue.';
    if (paidResource && forecast?.policy_version === 3 && forecast.tier !== 'carded')
        return 'Connect a card to unlock paid resources.';
    if (locked && isTrial) return reason ?? 'Not available on limited trial';
    return null;
}

const TrialLock: React.FC<TrialLockProps> = ({ locked = true, paidResource = locked, reason, children }) => {
    const tooltip = useResourceCreationLock(locked, paidResource, reason);
    if (!tooltip) return children;

    const child = React.Children.only(children) as React.ReactElement<any>;
    const existingLabel = child.props['aria-label'] || child.props.title;
    const disabledProps = {
        className: [child.props.className, 'trial-locked'].filter(Boolean).join(' '),
        'data-trial-tooltip': tooltip,
        'aria-disabled': true as const,
        'aria-label': existingLabel ? `${existingLabel}. ${tooltip}` : undefined,
        title: tooltip,
        onClick: (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
        },
    };
    // A prevented click still leaves links usable through middle-click, context menus
    // and browser navigation. Remove the destination entirely while access is locked.
    if (child.type === Link || child.type === 'a') {
        return <span className={disabledProps.className} role="link" tabIndex={0}
            aria-disabled="true" aria-label={disabledProps['aria-label']}
            title={tooltip} data-trial-tooltip={tooltip}>{child.props.children}</span>;
    }
    return React.cloneElement(child, {...disabledProps, disabled: true});
};

export default TrialLock;
